import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type BomPayload, type BomResult, type MappingGate } from '../api/client';
import { useLang } from '../i18n/lang';

// Internal BOM (§5.9). Full internal detail — material cost, labor hours, source,
// confidence. INTERNAL ONLY: proposals never show this. Generated by cascading
// human counts through device assemblies. Pricing/markup land in Phase 6.
const money = (n: number | null | undefined) => n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (n: number | null | undefined) => n == null ? '—' : n.toFixed(2);

export function Bom() {
  const { t } = useLang();
  const { id = '' } = useParams();
  const [data, setData] = useState<BomPayload | null>(null);
  const [result, setResult] = useState<BomResult | null>(null);
  const [gate, setGate] = useState<MappingGate | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const [bom, mp] = await Promise.all([api.getBom(id), api.getMappings(id).catch(() => null)]);
      setData(bom);
      setGate(mp?.gate ?? null);
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load BOM', fil: 'Hindi na-load ang BOM' })); }
  }
  useEffect(() => { void load(); }, [id]);

  // Prerequisite state for generating a BOM.
  const noSymbols = gate != null && gate.total === 0;
  const needsMapping = gate != null && gate.blocks_bom;
  const blocked = noSymbols || needsMapping;

  async function generate() {
    setBusy(true); setError(''); setResult(null);
    try {
      const res = await api.generateBom(id);
      setResult(res.result);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'BOM generation failed', fil: 'Nabigo ang pag-generate ng BOM' })); }
    finally { setBusy(false); }
  }

  const lines = data?.lines ?? [];
  const totals = data?.totals;

  return (
    <div>
      <Link to={`/projects/${id}`} className="muted" style={{ fontSize: 13 }}>← {t({ en: 'Scope Analysis', fil: 'Scope Analysis' })}</Link>
      <h1 style={{ margin: '4px 0 0' }}>{t({ en: 'Internal BOM', fil: 'Internal BOM' })}</h1>
      <div className="banner" style={{ marginTop: 8 }}>
        <strong>{t({ en: 'Internal only.', fil: 'Internal lang.' })}</strong>{' '}
        {t({
          en: 'Full material, labor and source detail. This is never shown to customers — proposals carry the final number alone. Quantities came from your count sheet; the software never counts.',
          fil: 'Kompletong material, labor at source detail. Hindi ito ipinapakita sa mga customer — ang final na numero lang ang nasa proposals. Galing sa iyong count sheet ang quantities; hindi nagbibilang ang software.',
        })}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>{t({ en: 'Generate from counts', fil: 'I-generate mula sa counts' })}</h2>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              {t({
                en: 'Cascades each counted symbol → confirmed device → assembly parts. Material is internet-estimated until distributor pricing (Phase 6); labor is NECA MLU / BICSI hours.',
                fil: 'Kina-cascade ang bawat counted symbol → confirmed device → assembly parts. Internet-estimated ang material hanggang sa distributor pricing (Phase 6); ang labor ay NECA MLU / BICSI hours.',
              })}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={generate} disabled={busy || blocked}
              title={needsMapping ? t({ en: 'Resolve symbol → device mappings first', fil: 'Ayusin muna ang symbol → device mappings' }) : noSymbols ? t({ en: 'Analyze documents first', fil: 'I-analyze muna ang mga dokumento' }) : ''}>
              {busy ? t({ en: 'Generating…', fil: 'Nire-generate…' }) : t({ en: 'Generate BOM', fil: 'I-generate ang BOM' })}
            </button>
            <Link className="btn btn-ghost" to={`/projects/${id}/costing`}>{t({ en: 'Pricing & labor →', fil: 'Pricing at labor →' })}</Link>
          </div>
        </div>

        {/* Prerequisite guidance — show WHERE to act instead of a bare 409. */}
        {needsMapping && (
          <div className="banner banner-danger" style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <strong>{gate!.needs_review} symbol{gate!.needs_review === 1 ? '' : 's'} {t({ en: 'still need a device mapping.', fil: 'ang kailangan pa ng device mapping.' })}</strong>{' '}
              {t({ en: 'Every counted symbol must be matched to a device before the BOM can cascade.', fil: 'Bawat counted symbol ay dapat ma-match sa isang device bago mag-cascade ang BOM.' })}
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                {gate!.confirmed} {t({ en: 'confirmed', fil: 'confirmed' })} · {gate!.proposed} {t({ en: 'proposed', fil: 'proposed' })} · {gate!.needs_review} {t({ en: 'need review', fil: 'kailangang i-review' })} · {gate!.excluded} {t({ en: 'excluded', fil: 'excluded' })}
              </div>
            </div>
            <Link className="btn btn-primary" to={`/projects/${id}/mapping`}>{t({ en: 'Resolve mappings →', fil: 'Ayusin ang mappings →' })}</Link>
          </div>
        )}
        {noSymbols && (
          <div className="banner banner-danger" style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div><strong>{t({ en: 'No symbols yet.', fil: 'Wala pang symbols.' })}</strong> {t({ en: 'Analyze the plan documents first, then count and map them.', fil: 'I-analyze muna ang mga plan document, tapos bilangin at i-map ang mga ito.' })}</div>
            <Link className="btn btn-primary" to={`/projects/${id}`}>{t({ en: 'Go to Scope Analysis →', fil: 'Pumunta sa Scope Analysis →' })}</Link>
          </div>
        )}

        {error && <div className="error" style={{ marginTop: 10 }}>{error}</div>}
        {result && (
          <div className="banner" style={{ marginTop: 12 }}>
            <strong>{result.lines} {t({ en: 'line items.', fil: 'line items.' })}</strong> {t({ en: 'Material', fil: 'Material' })} {money(result.total_material)} · {t({ en: 'Labor', fil: 'Labor' })} {hrs(result.total_labor_hours)} {t({ en: 'hrs.', fil: 'hrs.' })}
            {result.uncounted.length > 0 && <div style={{ color: 'var(--warning)' }}>{t({ en: 'Skipped (no count):', fil: 'Nilaktawan (walang count):' })} {result.uncounted.join(', ')}</div>}
            {result.no_assembly.length > 0 && <div style={{ color: 'var(--warning)' }}>{t({ en: 'No catalog assembly:', fil: 'Walang catalog assembly:' })} {result.no_assembly.join(', ')}</div>}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2>{t({ en: 'Bill of materials', fil: 'Bill of materials' })}</h2>
          {totals && totals.line_count > 0 && (
            <div className="muted">{totals.line_count} {t({ en: 'lines', fil: 'lines' })} · {t({ en: 'material', fil: 'material' })} <strong>{money(totals.material)}</strong> · {t({ en: 'labor', fil: 'labor' })} <strong>{hrs(totals.labor_hours)} {t({ en: 'hrs', fil: 'hrs' })}</strong></div>
          )}
        </div>
        {!lines.length && <p className="muted">{t({ en: 'No BOM yet. Resolve mappings, import counts, then Generate BOM.', fil: 'Wala pang BOM. Ayusin ang mappings, i-import ang counts, tapos I-generate ang BOM.' })}</p>}
        {lines.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>System</th><th>Part #</th><th>Mfr</th><th>Description</th><th>Qty</th><th>Unit</th>
                  <th>Unit mat.</th><th>Ext. mat.</th><th>Labor u/hrs</th><th>Ext. hrs</th>
                  <th>Labor cat.</th><th>Source</th><th>Conf.</th><th>Ref</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td className="muted">{l.system ?? '—'}</td>
                    <td><strong>{l.part_number}</strong></td>
                    <td>{l.manufacturer}</td>
                    <td>{l.description}</td>
                    <td>{l.qty}</td>
                    <td className="muted">{l.unit}</td>
                    <td>{money(l.unit_material_cost)}</td>
                    <td>{money(l.extended_material_cost)}</td>
                    <td>{hrs(l.labor_unit_hours)}</td>
                    <td>{hrs(l.extended_labor_hours)}</td>
                    <td className="muted">{l.labor_category}</td>
                    <td><span className="pill pill-proposed">{l.price_source}</span></td>
                    <td>{l.confidence_score != null ? `${Math.round(l.confidence_score * 100)}%` : '—'}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{l.source_reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
